import { db } from "@/lib/supabase";
import type { Client } from "@/hooks/useClientsData";
import { fuzzyScore } from "@/utils/fuzzySearch";
import { useEffect, useMemo, useRef, useState } from "react";

export interface ClientSelection {
  id: string | null;
  name: string;
}

interface ClientComboboxProps {
  value: ClientSelection;
  onChange: (value: ClientSelection) => void;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
}

/**
 * Client picker for the project forms. On focus it opens a dropdown of existing
 * clients; typing runs a fuzzy name search to filter it. Selecting an option
 * links the project to that client (id + name). Pressing Enter on a name that
 * matches no existing client creates the client immediately (details can be
 * filled in later on the Clients page) and selects it.
 *
 * Reuses the `.combobox__*` styles from the shared CreatableComboBox.
 */
export function ClientCombobox({
  value,
  onChange,
  disabled = false,
  placeholder = "Select or type a client...",
  id,
}: ClientComboboxProps) {
  const [clients, setClients] = useState<Client[]>([]);
  const [searchTerm, setSearchTerm] = useState(value.name);
  const [isOpen, setIsOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load the client list once.
  useEffect(() => {
    let cancelled = false;
    db.clients.getAll().then(({ data }) => {
      if (!cancelled && data) setClients(data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Reflect external changes to the selected client (e.g. modal reopened).
  useEffect(() => {
    setSearchTerm(value.name);
  }, [value.id, value.name]);

  // Fuzzy-filter + rank options by the typed term; show all when the term
  // equals the current selection (i.e. the user just focused without typing).
  const filteredClients = useMemo(() => {
    const term = searchTerm.trim();
    if (!term || term === value.name) return clients;
    return clients
      .map((client) => ({ client, score: fuzzyScore(client.name, term) }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((entry) => entry.client);
  }, [clients, searchTerm, value.name]);

  const trimmed = searchTerm.trim();
  const isNewValue =
    trimmed.length > 0 &&
    !clients.some((client) => client.name.toLowerCase() === trimmed.toLowerCase());

  const selectClient = (client: Client) => {
    onChange({ id: client.id, name: client.name });
    setSearchTerm(client.name);
    setIsOpen(false);
  };

  const createAndSelect = async () => {
    if (!isNewValue || creating) return;
    setCreating(true);
    try {
      const { data, error } = await db.clients.create({ name: trimmed });
      if (error || !data) throw error ?? new Error("Failed to create client");
      setClients((prev) =>
        [...prev, data].sort((a, b) => a.name.localeCompare(b.name)),
      );
      onChange({ id: data.id, name: data.name });
      setSearchTerm(data.name);
      setIsOpen(false);
    } catch (err) {
      console.error("Failed to create client", err);
    } finally {
      setCreating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      // Don't submit the surrounding form; resolve the client selection instead.
      e.preventDefault();
      if (isNewValue) {
        void createAndSelect();
      } else {
        const exact = clients.find(
          (client) => client.name.toLowerCase() === trimmed.toLowerCase(),
        );
        if (exact) selectClient(exact);
      }
    } else if (e.key === "Escape") {
      setSearchTerm(value.name);
      setIsOpen(false);
    }
  };

  // Click outside: close and revert the text to the committed selection so a
  // half-typed name never lingers (creation only happens via Enter/click).
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearchTerm(value.name);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value.name]);

  return (
    <div ref={containerRef} className="combobox">
      <input
        id={id}
        type="text"
        className="combobox__input"
        placeholder={placeholder}
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        disabled={disabled || creating}
        autoComplete="off"
      />

      {isOpen && (
        <div className="combobox__dropdown">
          {filteredClients.map((client) => (
            <button
              key={client.id}
              type="button"
              className="combobox__option"
              onClick={() => selectClient(client)}
            >
              {client.name}
            </button>
          ))}

          {isNewValue && (
            <>
              <hr className="combobox__divider" />
              <button
                type="button"
                className="combobox__option combobox__option--create"
                onClick={() => void createAndSelect()}
                disabled={creating}
              >
                ➕ Create "{trimmed}"
              </button>
            </>
          )}

          {filteredClients.length === 0 && !isNewValue && (
            <div className="combobox__empty">No clients found</div>
          )}
        </div>
      )}
    </div>
  );
}
